import { BankWithdrawResponse } from 'models/bankModel';
import { useEffect, useMemo, useState } from 'react';
import walletApi from 'apis/walletApi';
import usdtApi from 'apis/usdtApi';
import toast from 'react-hot-toast';
import { NumericFormat } from 'react-number-format';
import { css } from '@emotion/css';
import { colors } from 'constants/theme';
import moment from 'moment';
import userApi from 'apis/userApi';
import { WalletHistoryPayload, WithdrawApplyPayload } from 'models/walletModel';
import { subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { removeSeperateNumber } from 'utils/function';
import LoadingIcon from 'components/loading/LoadingIcon';
import useBalance from 'hooks/useBalance';
import { isAddress } from 'ethers';

const wdrStatus = ['Đã gửi', 'Đang xử lý', 'Chờ duyệt', '', 'Thành công', 'Thất bại'];

const parseJsonString = <T,>(value: any, fallback: T): T => {
    if (!value) return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const getUsdtAddress = (item: BankWithdrawResponse) => {
    if (item.address) return item.address;
    if (item.wallet && isAddress(item.wallet)) return item.wallet;
    if (item.network && isAddress(item.network)) return item.network;
    return item.bankcard || item.wallet || item.network || '';
};

const getUsdtNetwork = (item: BankWithdrawResponse) => {
    if (item.network && !isAddress(item.network)) return item.network;
    if (item.wallet && !isAddress(item.wallet)) return item.wallet;
    return item.bankname || 'USDT';
};

interface WithdrawInfo {
    wmin: number;
    wmax: number;
    wdhours: number;
    mrtxcs: number;
    wtimes: number;
}

function WithdrawPage() {
    const navigate = useNavigate();
    const [bankList, setBankList] = useState<BankWithdrawResponse[]>([]);
    const [usdtWalletList, setUsdtWalletList] = useState<BankWithdrawResponse[]>([]);
    const [withdrawType, setWithdrawType] = useState<'bank' | 'usdt'>('bank');
    const [bankIdSelect, setBankIdSelect] = useState<number>(0);
    const [usdtAddress, setUsdtAddress] = useState('');
    const [usdtExchangeRate, setUsdtExchangeRate] = useState(0);
    const [amountWithdraw, setAmountWithdraw] = useState('');
    const [withdrawInfo, setWithdrawInfo] = useState<WithdrawInfo>({ wmin: 50000, wmax: 100000000, wdhours: 6, mrtxcs: 5, wtimes: 0 });
    const [withdrawHistory, setWithdrawHistory] = useState<any[]>([]);
    const [passPay, setPassPay] = useState('');
    const [loading, setLoading] = useState(false);

    const loadBalanceMain = useBalance();

    useEffect(() => {
        const getBankList = async () => {
            try {
                const { data } = await walletApi.withdrawal({});
                if (!data.msg) {
                    const { wmin, wmax, mrtxcs, wdhours, wtimes } = data;
                    const bankItems = (data.wlist || []).filter((item: any) => Number(item.type) === 0);
                    const usdtItems = (data.wlist || []).filter((item: any) => Number(item.type) === 1);
                    setWithdrawInfo({ wmin, wmax, mrtxcs, wdhours, wtimes });
                    setBankList(bankItems);
                    setUsdtWalletList(usdtItems);

                    if (bankItems.length > 0) {
                        setBankIdSelect(bankItems[0].id);
                    }
                    if (usdtItems.length > 0) {
                        setUsdtAddress(getUsdtAddress(usdtItems[0]));
                    }
                    if ((data.wlist || []).length > 0) {
                        getHistoryWithdraw();
                    }
                } else {
                    toast.error(data.msg);
                }

                if ((data.wlist || []).length === 0) {
                    // setShowAddBank(true);
                    navigate('/account?mode=bank-account');
                }
            } catch (error) {
                console.log(error);
            }
        };
        getBankList();
    }, []);

    useEffect(() => {
        const getUsdtRate = async () => {
            try {
                const { data } = await usdtApi.getNetworks();
                const configs = parseJsonString<Record<string, string>[]>(data.usdtConfigs, []);
                const payoutConfig = configs.find((item) => Object.prototype.hasOwnProperty.call(item, 'RATE_PAYOUT'));
                const payinConfig = configs.find((item) => Object.prototype.hasOwnProperty.call(item, 'RATE_PAYIN'));
                setUsdtExchangeRate(Number(payoutConfig?.RATE_PAYOUT || payinConfig?.RATE_PAYIN) || 0);
            } catch (error) {
                console.log(error);
            }
        };
        getUsdtRate();
    }, []);

    const usdtReceiveAmount = useMemo(() => {
        const amount = Number(removeSeperateNumber(amountWithdraw)) || 0;
        if (!usdtExchangeRate) return 0;
        return amount / usdtExchangeRate;
    }, [amountWithdraw, usdtExchangeRate]);

    const getHistoryWithdraw = async () => {
        const startDate = moment(subDays(new Date(), 30)).format('yyyy-MM-DD');
        let payload: WalletHistoryPayload = {
            stime: `${startDate} 00:00:00`,
            etime: moment(new Date()).format('yyyy-MM-DD HH:mm:ss'),
        };
        try {
            const { data } = await userApi.withdrawalSearch(payload, 'btnSearch');
            if (!data.msg) {
                setWithdrawHistory(data.reslist.slice(0, 10));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const submitWithdraw = async () => {
        if (withdrawInfo.mrtxcs === withdrawInfo.wtimes) {
            toast.error('Đã quá giới hạn rút tiền trong một ngày');
            return;
        }
        if (!amountWithdraw || Number(amountWithdraw) === 0) {
            toast.error('Vui lòng nhập số tiền');
            return;
        }
        if (Number(removeSeperateNumber(amountWithdraw)) < withdrawInfo.wmin) {
            toast.error(`Số tiền rút tối thiểu là ${new Intl.NumberFormat().format(withdrawInfo.wmin)}`);
            return;
        }
        if (Number(removeSeperateNumber(amountWithdraw)) > withdrawInfo.wmax) {
            toast.error(`Số tiền rút tối đa là ${new Intl.NumberFormat().format(withdrawInfo.wmax)}`);
            return;
        }
        if (!passPay) {
            toast.error('Vui lòng nhập mật khẩu thanh toán');
            return;
        }
        if (withdrawType === 'usdt') {
            await submitWithdrawUsdt();
            return;
        }
        setLoading(true);
        const payload: WithdrawApplyPayload = {
            cardid: bankIdSelect,
            checkpwd: passPay,
            wamount: removeSeperateNumber(amountWithdraw),
        };
        try {
            const { data } = await walletApi.withdrawalApply(payload);
            if (!data.msg) {
                const { mrtxcs, wdhours, wtimes } = data;
                const newWithdrawInfo = { ...withdrawInfo, mrtxcs, wdhours, wtimes };
                setWithdrawInfo(newWithdrawInfo);
                toast.success('Gửi yêu cầu rút tiền thành công');
                loadBalanceMain();
                setPassPay('');
                setAmountWithdraw('');

                setTimeout(() => {
                    getHistoryWithdraw();
                }, 1000);
            } else {
                loadBalanceMain();
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const submitWithdrawUsdt = async () => {
        if (!amountWithdraw || Number(amountWithdraw) === 0) {
            toast.error('Vui lòng nhập số tiền');
            return;
        }
        if (!usdtAddress.trim()) {
            toast.error('Vui lòng nhập địa chỉ ví USDT');
            return;
        }
        if (!passPay) {
            toast.error('Vui lòng nhập mật khẩu thanh toán');
            return;
        }
        setLoading(true);
        try {
            const { data } = await walletApi.withdrawalUsdt({ amount: removeSeperateNumber(amountWithdraw), address: usdtAddress.trim(), remark: '', checkpwd: passPay });
            if (!data.msg) {
                toast.success('Gửi yêu cầu rút USDT thành công');
                loadBalanceMain();
                setPassPay('');
                setAmountWithdraw('');
                setTimeout(() => {
                    getHistoryWithdraw();
                }, 1000);
            } else {
                loadBalanceMain();
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={styles.wrap}>
                <ul className={styles.listGuide}>
                    <li>
                        <img alt="" src={require('assets/images/icons/icon-new-check.png')} />
                        Mỗi ngày chỉ rút <span>({withdrawInfo.mrtxcs})</span> lần, bạn đã yêu cầu thành công <span>({withdrawInfo.wtimes}) </span> lần trong hôm nay
                    </li>
                    <li>
                        <img alt="" src={require('assets/images/icons/icon-new-check.png')} />
                        Rút tiền <span>({'24/7.'})</span>
                    </li>
                    <li>
                        <img alt="" src={require('assets/images/icons/icon-new-check.png')} />
                        Tài khoản ngân hàng mới thêm cần <span>{withdrawInfo.wdhours}</span> tiếng trở lên mới có thể rút tiền.
                    </li>
                </ul>

                <div className={styles.containerInfo}>
                    <div className={styles.typeTabs}>
                        <button type="button" className={withdrawType === 'bank' ? 'active' : ''} onClick={() => setWithdrawType('bank')}>
                            Rút ngân hàng
                        </button>
                        <button type="button" className={withdrawType === 'usdt' ? 'active' : ''} onClick={() => setWithdrawType('usdt')}>
                            Rút USDT
                        </button>
                    </div>
                    {withdrawType === 'bank' ? <div className="itemInfo">
                        <div className="title">
                            <p>Ngân hàng nhận tiền:</p>
                        </div>
                        <div className="content">
                            <div className={styles.wrapInput}>
                                <select value={bankIdSelect} onChange={(e) => setBankIdSelect(Number(e.target.value))}>
                                    {bankList.map((item) => (
                                        <option value={item.id} key={item.id}>
                                            {item.bankname} | số cuối tài khoản {item.bankcard.slice(-4)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div> : <div className="itemInfo">
                        <div className="title">
                            <p>Ví USDT nhận tiền:</p>
                        </div>
                        <div className="content">
                            <div className={styles.wrapInput}>
                                {usdtWalletList.length > 0 ? (
                                    <select value={usdtAddress} onChange={(e) => setUsdtAddress(e.target.value)}>
                                        {usdtWalletList.map((item) => {
                                            const address = getUsdtAddress(item);
                                            return (
                                                <option value={address} key={item.id || address}>
                                                    {getUsdtNetwork(item)} | {address}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <input value={usdtAddress} onChange={(e) => setUsdtAddress(e.target.value)} placeholder="Nhập địa chỉ ví USDT" />
                                )}
                            </div>
                            {usdtWalletList.length === 0 && <p className="note">Bạn có thể thêm ví USDT tại mục Tài khoản ngân hàng.</p>}
                        </div>
                    </div>}
                    <div className="itemInfo">
                        <div className="title">
                            <p>Số tiền rút:</p>
                        </div>
                        <div className="content">
                            <div className={styles.wrapInput}>
                                <NumericFormat allowNegative={false} thousandSeparator="," placeholder="Nhập số tiền..." value={amountWithdraw} onChange={(e) => setAmountWithdraw(e.target.value)} />
                            </div>
                            <p className="note">
                                ( Số tiền tối thiểu <span>{new Intl.NumberFormat().format(withdrawInfo.wmin)}</span> VND / Số tiền tối đa{' '}
                                <span>{new Intl.NumberFormat().format(withdrawInfo.wmax)}</span> VND )
                            </p>
                            {withdrawType === 'usdt' && (
                                <div className={styles.usdtRateInfo}>
                                    <p>
                                        USDT nhận được: <span>{usdtReceiveAmount ? usdtReceiveAmount.toFixed(2) : '0'}</span>
                                    </p>
                                    <p>
                                        Tỷ giá quy đổi: 1USDT = <span>{usdtExchangeRate ? new Intl.NumberFormat().format(usdtExchangeRate) : 'Đang cập nhật'} VND</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="itemInfo">
                        <div className="title">
                            <p>Mật khẩu thanh toán:</p>
                        </div>
                        <div className="content">
                            <div className={styles.wrapInput}>
                                <input type="password" value={passPay} onChange={(e) => setPassPay(e.target.value)} />
                            </div>
                        </div>
                    </div>
                </div>

                <button type="button" className={styles.btnSubmit} onClick={submitWithdraw}>
                    Gửi đi
                </button>

                <p className={styles.noticeHistory}>
                    Danh sách hiển thị 10 lệnh rút trong 30 ngày gần nhất. Xem tất cả tại <span onClick={() => navigate('/account?mode=wl-depo-his&type=1')}>Lịch sử rút tiền</span>
                </p>

                {/* history withdraw table */}
                <div className={styles.containerTable}>
                    <div className={styles.table}>
                        <div className="row">
                            <p className="title col">STT</p>
                            <p className="title col">Mã Số</p>
                            <p className="title col">Giá Trị</p>
                            <p className="title col">Thời Gian</p>
                            <p className="title col">Trạng Thái</p>
                            <p className="title col">Ghi Chú</p>
                        </div>
                        {withdrawHistory.length > 0 ? (
                            withdrawHistory.map((item, index) => (
                                <div className="row" key={item.id}>
                                    <p className="col">{index + 1}</p>
                                    <p className="col">{item.id}</p>
                                    <p className="col">{new Intl.NumberFormat().format(item.amount)}</p>
                                    <p className="col">{moment(item.applytime).format('DD/MM/yyyy HH:mm')}</p>
                                    <p className={classNames('col', { 'text-error': [0, 1, 2, 5].includes(item.status) }, { 'text-success': item.status === 4 })}>{wdrStatus[item.status]}</p>
                                    <p className="col">{item.remark}</p>
                                </div>
                            ))
                        ) : (
                            <div className="no-data"></div>
                        )}
                    </div>
                </div>
            </div>

            {loading && <LoadingIcon />}
        </>
    );
}

const styles = {
    btnSubmit: css`
        border: 1px solid #c9820a !important;
        border-radius: 3px !important;
        color: #fff !important;
        background: linear-gradient(to bottom, #ffffff 0%, #ffc857 2%, #ffad33 100%);
        align-self: center;
        padding-left: 40px;
        padding-right: 40px;
        padding-top: 10px;
        padding-bottom: 10px;
        margin-top: 30px;
        margin-bottom: 30px;
    `,
    containerInfo: css`
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-top: 30px;
        .title {
            width: 350px;
            text-align: right;
            font-size: 18px;
            font-weight: 300;
            p {
                line-height: 40px;
            }
        }
        .itemInfo {
            display: flex;
            gap: 20px;
        }
        .content {
            text-align: left;

            .note {
                font-size: 12px;
                span {
                    color: #f49e1d;
                }
            }

            .valueMax {
                line-height: 40px;
            }
        }
    `,
    typeTabs: css`
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-bottom: 6px;
        button {
            border: 1px solid ${colors.primary};
            border-radius: 3px;
            background: #fff;
            color: ${colors.primary};
            padding: 8px 18px;
            cursor: pointer;
            &.active {
                background: ${colors.primary};
                color: #fff;
            }
        }
    `,
    usdtRateInfo: css`
        margin-top: 8px;
        font-size: 13px;
        line-height: 22px;
        span {
            color: #f49e1d;
            font-weight: 500;
        }
    `,
    wrap: css`
        color: black;
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        min-height: 800px;
        display: flex;
        flex-direction: column;
    `,
    listGuide: css`
        list-style-type: none;
        padding-left: 40px;
        color: #282c35;
        font-size: 18px;
        font-weight: 300;
        align-self: center;
        margin-top: 30px;
        gap: 10px;
        display: flex;
        flex-direction: column;
        li {
            margin-bottom: 8px;
        }
        span {
            color: ${colors.yellow};
            font-weight: 500;
        }
    `,
    noticeHistory: css`
        text-align: center;
        margin-top: 8px;
        font-size: 14px;
        background-image: linear-gradient(90deg, #e4bc62, #dec887, #e4bc62);
        padding: 7px;
        margin-bottom: 5px;
        font-size: 16px;
        font-weight: 300;
        span {
            text-decoration: underline;
            cursor: pointer;
        }
    `,
    wrapInput: css`
        color: black;
        display: flex;
        flex: 1;
        width: 350px;
        input {
            border: none;
            height: 40px;
            border-bottom: 1px solid black;
            padding-left: 10px;
            background-color: white;
            width: 100%;
        }
        select {
            border: none;
            background-color: white;
            height: 40px;
            border-bottom: 1px solid black;
            width: 100%;
            color: black;
            box-shadow: none;
            outline: none;
            padding-left: 5px;
            &:focus {
                border: none;
                box-shadow: none;
            }
        }
    `,
    containerTable: css`
        background-color: #f2f2f2;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        min-height: 250px;
        border-radius: 5px;
        padding: 4px 4px 18px;
        margin-bottom: 100px;
    `,
    table: css`
        border-radius: 5px;
        overflow: hidden;
        &.table-col-center {
            .col {
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
        .row {
            display: flex;
            border-bottom: 1px solid var(--border-table);
            background-color: #e2dfdc;
            &:first-child {
                border-bottom-color: white;
            }
            &:last-child {
                border-bottom: none;
            }
        }
        .col {
            flex: 1;
            padding: 12px 8px;
            text-align: center;
            font-size: 12.8px;
            font-weight: 300;
        }
        .text-left {
            text-align: left;
        }
        .title {
            color: white;
            font-weight: 300;
            border-right: 1px solid white;
            background-color: ${colors.primary};
            &:last-child {
                border-right: 0;
            }
        }
        .no-data {
            text-align: center;
            padding: 30px;
            height: 180px;
            p {
                margin-top: 8px;
                font-size: 14px;
            }
        }
    `,
};

export default WithdrawPage;
